import { ChangeDetectionStrategy, Component, Input, OnChanges, inject } from '@angular/core';
import { CsAnnotatedImageSpec, CsImageHighlight }              from '../cs-chat-segments';
import { CS_CHAT_CONFIG }                                      from '../chat.config';

/** A highlight resolved to inline style objects (box + optional label), computed once per change. */
interface RenderedHighlight {
	box:       { [k: string]: string };
	label:     { [k: string]: string } | null;
	labelText: string;
	tooltip:   string;
}

/** infoType → default color/border, mirroring the Docusaurus ImageHighlighter presets. */
const PRESETS: { [k: string]: { color: string; thickness: number; dashed: boolean } } = {
	danger:  { color: '#FF4D4F', thickness: 3, dashed: true },
	warning: { color: '#FAAD14', thickness: 3, dashed: true },
	info:    { color: '#1890FF', thickness: 3, dashed: true },
	success: { color: '#52C41A', thickness: 3, dashed: true },
};

/**
 * Read-only render of a documentation screenshot with annotation overlays — the chat counterpart of
 * the Docusaurus `ImageHighlighter` component (drawing/editing removed). Presentational only: it takes
 * a validated {@link CsAnnotatedImageSpec} (src already restricted to same-origin/data: by the parser)
 * and lays out percentage-positioned boxes + labels over the image. Click toggles a full-screen zoom.
 */
@Component({
	standalone:      false,
	selector:        'cs-annotated-image',
	templateUrl:     './cs-annotated-image.component.html',
	styleUrls:       ['./cs-annotated-image.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsAnnotatedImageComponent implements OnChanges {

	@Input() spec!: CsAnnotatedImageSpec;

	boxes: RenderedHighlight[] = [];
	resolvedSrc = '';
	zoomed = false;

	// The chat config carries the MCP base (e.g. "/ai/api/chat" embedded, "/api/chat" standalone).
	private readonly config = inject(CS_CHAT_CONFIG, { optional: true });

	ngOnChanges(): void {
		this.boxes = (this.spec?.highlights ?? []).map(h => this.render(h));
		this.resolvedSrc = this.resolveSrc(this.spec?.src);
		this.zoomed = false;
	}

	/**
	 * Resolves the server-relative image path ("/api/docs/img/…") against the MCP root derived from the
	 * chat apiUrl, so the <img> loads from the chat backend, not the host page's origin (which matters
	 * when the chat is embedded under a path base like /ai). data: URIs pass through unchanged.
	 */
	private resolveSrc(src: string | undefined): string {
		if (!src) { return ''; }
		if (src.startsWith('data:')) { return src; }
		const root = (this.config?.apiUrl ?? '').replace(/\/api\/chat\/?$/, '');   // "/ai/api/chat" → "/ai"
		return src.startsWith('/') ? `${root}${src}` : src;
	}

	toggleZoom(): void { this.zoomed = !this.zoomed; }

	trackBox = (i: number, _: RenderedHighlight) => i;

	private render(h: CsImageHighlight): RenderedHighlight {
		const preset    = PRESETS[h.infoType ?? 'info'] ?? PRESETS['info'];
		const color     = h.color || preset.color;
		const thickness = h.thickness ?? preset.thickness;
		const dashed    = h.dashed ?? preset.dashed;
		const radius    = h.radius ?? 6;
		const fill      = hexToRgba(lighten(color, 0.8), h.solidOpacity ?? 0);

		const box: { [k: string]: string } = {
			// Position/box-model are inline (not only in the SCSS) so overlays render correctly even
			// if the component stylesheet isn't applied by the host's build pipeline.
			position:        'absolute',
			'box-sizing':    'border-box',
			left:            `${h.x}%`,
			top:             `${h.y}%`,
			width:           `${h.width}%`,
			height:          `${h.height}%`,
			border:          `${thickness}px ${dashed ? 'dashed' : 'solid'} ${color}`,
			'border-radius': `${radius}px`,
			background:      fill,
		};
		return {
			box,
			label:     h.label ? this.labelStyle(h, color) : null,
			labelText: h.label ?? '',
			tooltip:   h.tooltip ?? '',
		};
	}

	private labelStyle(h: CsImageHighlight, color: string): { [k: string]: string } {
		const base: { [k: string]: string } = {
			// Full label chrome inline (see box note) so labels render correctly without the SCSS.
			position:        'absolute',
			'white-space':   'nowrap',
			'font-weight':   '600',
			'font-size':     '0.8rem',
			'line-height':   '1.2',
			padding:         '2px 6px',
			'border-radius': '6px',
			'pointer-events': 'none',
			'max-width':     '96vw',
			background:       hexToRgba(lighten(color, 0.8), h.labelBgOpacity ?? 0.87),
			color,
		};

		// A centered top/bottom label overflows the image when its box sits near an edge. Re-anchor it
		// to the box's near side (right-aligned when the box is right, left-aligned when left) so the
		// label grows INWARD and stays fully visible instead of spilling out of the chat.
		const centerX = h.x + h.width / 2;
		const horiz: { [k: string]: string } =
			centerX > 72 ? { right: '0', transform: 'none' } :
			centerX < 28 ? { left: '0',  transform: 'none' } :
			               { left: '50%', transform: 'translateX(-50%)' };

		switch (h.labelPosition) {
			case 'inside': return { ...base, left: '6px', top: '6px' };
			case 'bottom': return { ...base, ...horiz, top: 'calc(100% + 6px)' };
			// Side labels flip to the opposite side when the box hugs that edge, so they point inward.
			case 'left':   return h.x < 22
				? { ...base, left: '100%', top: '50%', transform: 'translateY(-50%)', 'margin-left': '6px' }
				: { ...base, right: '100%', top: '50%', transform: 'translateY(-50%)', 'margin-right': '6px' };
			case 'right':  return (h.x + h.width) > 78
				? { ...base, right: '100%', top: '50%', transform: 'translateY(-50%)', 'margin-right': '6px' }
				: { ...base, left: '100%', top: '50%', transform: 'translateY(-50%)', 'margin-left': '6px' };
			case 'top':
			default:       return { ...base, ...horiz, bottom: 'calc(100% + 6px)' };
		}
	}
}

// ── color helpers (ported from ImageHighlighter/index.tsx) ────────────────────

function hexToRgba(hex: string, alpha: number): string {
	const h = (hex || '').replace('#', '');
	let r = 0, g = 0, b = 0;
	if (h.length === 3)      { r = p(h[0] + h[0]); g = p(h[1] + h[1]); b = p(h[2] + h[2]); }
	else if (h.length === 6) { r = p(h.slice(0, 2)); g = p(h.slice(2, 4)); b = p(h.slice(4, 6)); }
	else                     { return `rgba(0,0,0,${alpha})`; }
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Lighten a hex color towards white by `amount` (0–1), for a soft fill/label background. */
function lighten(hex: string, amount: number): string {
	const h = (hex || '').replace('#', '');
	let r = 0, g = 0, b = 0;
	if (h.length === 3)      { r = p(h[0] + h[0]); g = p(h[1] + h[1]); b = p(h[2] + h[2]); }
	else if (h.length === 6) { r = p(h.slice(0, 2)); g = p(h.slice(2, 4)); b = p(h.slice(4, 6)); }
	else                     { return '#FFFFFF'; }
	r = Math.min(255, Math.floor(r + (255 - r) * amount));
	g = Math.min(255, Math.floor(g + (255 - g) * amount));
	b = Math.min(255, Math.floor(b + (255 - b) * amount));
	return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

function p(hex2: string): number { const n = parseInt(hex2, 16); return Number.isNaN(n) ? 0 : n; }
